import { GameState, SkiGameStateEnum } from "../ski";

export default class Skier {
    readonly gameState: GameState;
    readonly spriteImg = new Image();

    // Sprite dimensions (330x260, 5 frames in 3x2 grid)
    readonly spriteWidth = 110;
    readonly spriteHeight = 130;

    // Position (skier stays at bottom)
    readonly basePosY = 320;
    pos = { x: 250, y: this.basePosY };

    // Movement physics
    speed: number = 0;                    // Global speed in m/s (vector magnitude)
    readonly baseAcceleration: number = 1; // m/s² (gravity component down the slope)
    // Drag coefficient: at terminal velocity, baseAcceleration = drag * v²
    // For v_max = 25 m/s: drag = 1 / 625 = 0.0016
    readonly dragCoefficient: number = 0.0016;
    readonly tuckDragReduction: number = 1.5; // Tucking divides drag by this factor
    // Terminal velocity when tucking: sqrt(1 / (0.0016 / 1.5)) ≈ 30.6 m/s

    // Angle-based movement
    angle: number = 0;                    // Current angle in radians
    readonly maxAngle: number = Math.PI * 0.45;  // Max angle (~81 degrees)
    readonly turnSpeed: number = 3;       // How fast angle changes (rad/s)

    // Lateral movement scale (pixels per meter)
    readonly lateralScale: number = 25;

    // Sprite frames
    currentFrame: number = 1;

    // Input state
    inputDirection: -1 | 0 | 1 = 0;
    isTucking: boolean = false;           // True when pressing up arrow (tout schuss)

    constructor(gameState: GameState) {
        this.gameState = gameState;
        this.spriteImg.src = '/skier spritesheet simple.png';
    }

    // Descent speed (forward component)
    get descentSpeed(): number {
        return this.speed * Math.cos(this.angle);
    }

    // Lateral speed (sideways component)
    get lateralSpeed(): number {
        return this.speed * Math.sin(this.angle);
    }

    reset() {
        this.pos = { x: 250, y: this.basePosY };
        this.speed = 0;
        this.angle = 0;
        this.currentFrame = 1;
        this.inputDirection = 0;
        this.isTucking = false;
    }

    update(deltaTime: number) {
        if (this.gameState.gameState !== SkiGameStateEnum.racing) return;

        const dt = deltaTime / 1000;

        // Update angle based on input (slower when tucking)
        if (this.inputDirection !== 0) {
            const effectiveTurnSpeed = this.isTucking ? this.turnSpeed * 0.3 : this.turnSpeed;
            this.angle += this.inputDirection * effectiveTurnSpeed * dt;
            this.angle = Math.max(-this.maxAngle, Math.min(this.maxAngle, this.angle));
        }

        // Determine if actually in tuck position (pressing up AND going straight)
        const angleThreshold = this.maxAngle * 0.2;
        const isActuallyTucking = this.isTucking && Math.abs(this.angle) <= angleThreshold;

        // Calculate aerodynamic drag (reduced when tucking)
        const effectiveDrag = isActuallyTucking
            ? this.dragCoefficient / this.tuckDragReduction
            : this.dragCoefficient;
        const dragForce = effectiveDrag * this.speed * this.speed;

        // Net acceleration = gravity component - drag
        const netAcceleration = this.baseAcceleration - dragForce;
        this.speed = Math.max(0, this.speed + netAcceleration * dt);

        // Move laterally based on lateral speed component
        this.pos.x += this.lateralSpeed * this.lateralScale * dt;

        // Clamp to screen bounds
        const margin = 50;
        this.pos.x = Math.max(margin, Math.min(500 - margin, this.pos.x));

        // Update sprite frame based on angle and tuck state
        if (this.angle < -angleThreshold) {
            this.currentFrame = 0; // Turn left
        } else if (this.angle > angleThreshold) {
            this.currentFrame = 2; // Turn right
        } else if (this.isTucking) {
            this.currentFrame = 4; // Tuck position (tout schuss)
        } else {
            this.currentFrame = 1; // Neutral
        }
    }

    onCollision() {
        // Sudden speed decrease on tree hit
        this.speed = Math.max(0, this.speed * 0.3);
        this.gameState.treeHits++;
    }

    getCollisionBox(): { x: number; y: number; width: number; height: number } {
        const width = 20;
        const height = 10;
        return {
            x: this.pos.x - width / 2,
            y: this.pos.y - height / 2,
            width,
            height
        };
    }

    draw(ctx: CanvasRenderingContext2D, zoom: number) {
        if (!this.spriteImg.complete) return;

        const col = this.currentFrame % 3;
        const row = Math.floor(this.currentFrame / 3);

        const sourceX = col * this.spriteWidth;
        const sourceY = row * this.spriteHeight;

        const drawWidth = this.spriteWidth * 0.6;
        const drawHeight = this.spriteHeight * 0.6;

        ctx.drawImage(
            this.spriteImg,
            sourceX, sourceY,
            this.spriteWidth, this.spriteHeight,
            zoom * (this.pos.x - drawWidth / 2),
            zoom * (this.pos.y - drawHeight / 2),
            zoom * drawWidth,
            zoom * drawHeight
        );
    }
}
